import React, { useMemo } from 'react';
import { ConditionsNew } from '@retail/calculus/types';
import { Box, IconButton, Stack, Tooltip } from '@mui/material';
import PriceChangeOutlinedIcon from '@mui/icons-material/PriceChangeOutlined';
import { DataGridPremium, GridColDef } from '@mui/x-data-grid-premium';
import { getDataGridLocalization, MgPrisLocale } from '@retail/utils';
import { AddButton, Image, NoTableRowsOverlay, StackedTextCell } from '@retail/components';
import { useTranslation } from 'react-i18next';
import { useRowColorGrouping } from '@retail/hooks';
import { dataGridBoldClass } from '@shared/styles';
import { getHierarchyLevelLabel } from '@retail/calculus/utils';
import dayjs from 'dayjs';
import { useAppTFunction } from '@retail/app/i18n';
import { LevelChip } from '../ConditionApproval';
import { ConditionsTableActions } from '../ConditionsTableActions';

const baseColumnProps: Partial<GridColDef<ConditionsNew>> = {
  width: 140,
  sortable: false,
  headerAlign: 'left',
  align: 'left',
};

interface Props {
  roundingConditions: ConditionsNew[];
  loading: boolean;
  openPriceSimulation: (roundingConditionId: number) => void;
  openCreateDialog?: () => void;
  conditionActions?: {
    setDeactivateId: (agreement: number | undefined) => void;
    setEdit: (roundingCondition: ConditionsNew | undefined) => void;
    setDeleteId: (conditionId: number | undefined) => void;
  };
}

export const RoundingConditionsTable: React.FC<Props> = ({
  roundingConditions,
  loading,
  openPriceSimulation,
  openCreateDialog,
  conditionActions,
}) => {
  const { i18n } = useTranslation();
  const t = useAppTFunction();
  const { getRowColorForStripedTable } = useRowColorGrouping();

  const columns: GridColDef<ConditionsNew>[] = useMemo(() => {
    const baseColumns: GridColDef<ConditionsNew>[] = [
      {
        ...baseColumnProps,
        cellClassName: () => dataGridBoldClass,
        field: 'level',
        headerName: t('markup.level'),
        width: 190,
        valueGetter: (_, row) =>
          getHierarchyLevelLabel({
            t,
            bindingType: row.bindingType,
          }),
        renderCell: ({ row }) => {
          return <LevelChip bindingType={row.bindingType} active={!row.inDraft} />;
        },
      },
      {
        ...baseColumnProps,
        cellClassName: () => dataGridBoldClass,
        field: 'name',
        headerName: t('markup.levelOrItemName'),
        width: 260,
        valueGetter: (_, row) => `${row.bindingTargetName ?? ''} ${row.bindingTargetId ?? ''}`,
        renderCell: ({ row }) => (
          <StackedTextCell topLine={row.bindingTargetName} bottomLine={row.bindingTargetId} />
        ),
      },
      {
        ...baseColumnProps,
        cellClassName: () => dataGridBoldClass,
        field: 'ruleSet',
        headerName: t('calculus.rounding.ruleSetName'),
        width: 220,
        valueGetter: (_, row) => row.roundingCondition?.ruleSetId,
        renderCell: ({ row }) => (
          <Stack direction="row" alignItems="center" gap={1}>
            <span>{row.roundingCondition?.ruleSetName}</span>
            {row.roundingCondition?.centralOwner && (
              <Tooltip
                title={t('rounding.ruleOwner.central', {
                  owner: row.roundingCondition.centralOwner.name,
                })}
              >
                <Image
                  src={row.roundingCondition.centralOwner.imgUrl}
                  alt={row.roundingCondition.centralOwner.name}
                  width={24}
                  height={24}
                />
              </Tooltip>
            )}
          </Stack>
        ),
      },
      {
        ...baseColumnProps,
        field: 'activeFromTo',
        width: 140,
        headerName: t('markup.activeFromTo'),
        valueGetter: (_, row) => {
          const activeFrom = dayjs(row.roundingCondition?.validFrom).format('ll');
          const activeTo =
            row.roundingCondition?.validTo && dayjs(row.roundingCondition?.validTo).format('ll');
          return `${activeFrom}-${activeTo ?? ''}`;
        },
        renderCell: ({ row }) => {
          const activeFrom = dayjs(row.roundingCondition?.validFrom).format('ll');
          const activeTo =
            row.roundingCondition?.validTo && dayjs(row.roundingCondition?.validTo).format('ll');
          return <StackedTextCell topLine={activeFrom} bottomLine={activeTo} centerAlign />;
        },
      },
      {
        ...baseColumnProps,
        field: 'simulation',
        headerName: '',
        width: 50,
        disableColumnMenu: true,
        renderCell: ({ row }) => (
          <Tooltip title={t('rounding.simulation.title')}>
            <IconButton
              onClick={() =>
                row.roundingCondition ? openPriceSimulation(row.roundingCondition.ruleSetId) : null
              }
              color="primary"
              size="small"
            >
              <PriceChangeOutlinedIcon sx={{ width: 28, height: 28 }} />
            </IconButton>
          </Tooltip>
        ),
      },
    ];

    if (conditionActions) {
      baseColumns.push({
        ...baseColumnProps,
        field: 'actions',
        headerName: '',
        width: 90,
        disableColumnMenu: true,
        renderCell: ({ row }) => {
          const { setDeactivateId, setEdit, setDeleteId } = conditionActions;
          return (
            <ConditionsTableActions
              handleDeactivate={
                !row.inDraft ? () => setDeactivateId(row.roundingCondition!.conditionId) : undefined
              }
              handleEdit={() => setEdit(row)}
              handleDelete={
                !row.inDraft ? () => setDeleteId(row.roundingCondition!.conditionId) : undefined
              }
            />
          );
        },
      });
    }

    return baseColumns;
  }, [conditionActions, openPriceSimulation, t]);

  return (
    <Box sx={{ flexGrow: 1, position: 'relative' }}>
      <Box sx={{ position: 'absolute', inset: 0 }}>
        <DataGridPremium
          getRowId={(row) => row.roundingCondition!.conditionId}
          getRowClassName={getRowColorForStripedTable}
          columns={columns}
          rows={roundingConditions}
          rowHeight={70}
          loading={loading}
          initialState={{
            pinnedColumns: { right: ['simulation', 'actions'] },
          }}
          localeText={getDataGridLocalization(i18n.language as MgPrisLocale)}
          disableRowSelectionOnClick
          disableColumnResize
          disableColumnReorder
          disableColumnMenu
          hideFooter
          slots={{
            noRowsOverlay: () => (
              <NoTableRowsOverlay
                noRowsText={t('calculus.rounding.noConditions')}
                addAction={
                  <AddButton onClick={openCreateDialog} disabled={!openCreateDialog}>
                    {t('calculus.rounding.createCondition')}
                  </AddButton>
                }
              />
            ),
          }}
        />
      </Box>
    </Box>
  );
};
